import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type PhaseUploadMatchProps = {
  /** YYYY-MM (the HTML `<input type="month">` format). */
  period_month: string;
  selected_files: File[];

  customers_loading: boolean;
  customers_error: string | null;
  customers_count: number;

  matching_in_progress: boolean;

  onPeriodChange: (periodMonth: string) => void;
  /**
   * Called with the accepted (PDF-only) subset of the chosen files.
   * Non-PDFs are filtered out by this component and surfaced inline.
   */
  onAddFiles: (files: File[]) => void;
  /**
   * Discard the entire current file selection. Used when the operator
   * realises the wrong PDFs were chosen — per the spec, individual
   * file removal is not allowed.
   */
  onClearFiles: () => void;
  onRunMatch: () => void;
};

function isPdf(file: File): boolean {
  return file.name.toLowerCase().endsWith(".pdf");
}

/**
 * Phase 1 — Upload + Match.
 *
 * - Choose one period (YYYY-MM) that applies to every group.
 * - Choose one or more PDF files. Non-PDFs are filtered out inline; the
 *   operator cannot remove a single file once added — they must clear
 *   the whole selection if they picked wrong PDFs.
 * - "Match & continue" runs the matcher (in the parent) and transitions
 *   to Phase 2.
 *
 * No matching logic and no wizard state mutation lives here; both flow
 * through the parent via callbacks.
 */
export function PhaseUploadMatch({
  period_month,
  selected_files,
  customers_loading,
  customers_error,
  customers_count,
  matching_in_progress,
  onPeriodChange,
  onAddFiles,
  onClearFiles,
  onRunMatch,
}: PhaseUploadMatchProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [nonPdfRejectedCount, setNonPdfRejectedCount] = useState<number>(0);

  const handleFilesChosen = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    const pdfsOnly = incoming.filter(isPdf);
    const rejected = incoming.length - pdfsOnly.length;
    setNonPdfRejectedCount(rejected);
    if (pdfsOnly.length > 0) {
      onAddFiles(pdfsOnly);
    }
    // Reset the native input so picking the same file again still fires.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearFiles = () => {
    setNonPdfRejectedCount(0);
    onClearFiles();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Inline error/blocker computation. Order matters: the first blocker
  // shown is the one the operator should resolve first.
  const blockers: string[] = [];
  if (customers_loading) {
    blockers.push("Loading customers…");
  }
  if (customers_error !== null) {
    blockers.push(`Failed to load customers: ${customers_error}`);
  }
  if (period_month === "") {
    blockers.push("Choose a period.");
  }
  if (selected_files.length === 0) {
    blockers.push("Choose at least one PDF.");
  }

  const canRunMatch =
    !customers_loading &&
    customers_error === null &&
    period_month !== "" &&
    selected_files.length > 0 &&
    !matching_in_progress;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Choose period and PDFs</h2>
        <p className="text-sm text-muted-foreground">
          One period applies to every group. PDFs will be matched to
          customers in the next step.
        </p>
      </div>

      <div className="flex flex-col gap-2 max-w-sm">
        <label
          htmlFor="wizard-period-month"
          className="text-xs font-semibold text-muted-foreground"
        >
          Period (YYYY-MM)
        </label>
        <Input
          id="wizard-period-month"
          type="month"
          value={period_month}
          onChange={(e) => onPeriodChange(e.target.value)}
          disabled={matching_in_progress}
        />
        <span className="text-xs text-muted-foreground">
          Stored as the first day of the selected month.
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="wizard-file-input"
          className="text-xs font-semibold text-muted-foreground"
        >
          PDFs
        </label>
        <div className="flex flex-row items-center gap-2">
          <input
            id="wizard-file-input"
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={(e) => handleFilesChosen(e.target.files)}
            disabled={matching_in_progress}
            className="text-sm"
          />
          {selected_files.length > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearFiles}
              disabled={matching_in_progress}
            >
              Clear all
            </Button>
          ) : null}
        </div>
        {nonPdfRejectedCount > 0 ? (
          <div className="rounded border border-amber-500 bg-amber-50 p-2 text-xs text-amber-900">
            {nonPdfRejectedCount === 1
              ? "1 non-PDF file was skipped. Only .pdf files are accepted."
              : `${nonPdfRejectedCount} non-PDF files were skipped. Only .pdf files are accepted.`}
          </div>
        ) : null}
        {selected_files.length > 0 ? (
          <div className="border rounded p-2 text-sm flex flex-col gap-1 max-h-64 overflow-y-auto">
            <div className="text-xs text-muted-foreground">
              {selected_files.length} PDF
              {selected_files.length === 1 ? "" : "s"} selected. Individual
              files cannot be removed — use “Clear all” to start over.
            </div>
            <ul className="flex flex-col gap-0.5">
              {selected_files.map((f: File, idx: number) => (
                <li
                  key={`${f.name}-${idx}`}
                  className="font-mono text-xs break-all text-muted-foreground"
                >
                  {f.name}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {!customers_loading && customers_error === null ? (
        <div className="text-xs text-muted-foreground">
          {customers_count} customer{customers_count === 1 ? "" : "s"} loaded.
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <div className="rounded border border-muted bg-muted/30 p-2 text-xs text-muted-foreground flex flex-col gap-0.5">
          {blockers.map((b: string) => (
            <span key={b}>• {b}</span>
          ))}
        </div>
      ) : null}

      <div className="flex flex-row justify-end">
        <Button onClick={onRunMatch} disabled={!canRunMatch}>
          {matching_in_progress ? "Matching…" : "Match & continue"}
        </Button>
      </div>
    </div>
  );
}
